import Indicator from '../Indicator';
import subpixel from '../util/canvasSubpixelOffset';

/** Linear Regression indicator */
export default class LinearRegressionIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {soolean} noClip - Sets to not clip by default
	 */
	_getDefaultParams() {
		return {
			componentSeries: [{ id: 'linearregression' }],
			description:
				'The Linear Regression Indicator function returns the current value of a linear regression line of a field to time over a given number of periods.',
			id: 'linearregression',
			name: 'Linear Regression',
			noClip: true
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return null;
	}

	/** @returns {null} */
	getRangeMax() {
		return null;
	}

	/** @returns {object} */
	_getMarkers() {
		return {};
	}

	/**
	 * Linear Regression calculation
	 * see https://svn.wsod.local/svn/wsod/netframework/framework_repository/3.0-stable/Math/TechnicalIndicators.cs
	 * @param {object} data Indicator data
	 * @returns {object} Regression data object
	 */
	getLinearRegression(data) {
		let count = 1;
		let sumX = 0;
		let sumX2 = 0;
		let sumY = 0;
		let sumXY = 0;
		let temp = 0;
		const regressionData = {};
		const period = data.length;
		let seriesId = 'close';

		// use a different series id if based on a parent indicator
		if (
			this.params.parentUid ||
			this.params.parentUID
		) {
			const parentIndicator = this.core.getIndicatorByUid(
				this.params.parentUid ||
				this.params.parentUID
			);
			seriesId =
				parentIndicator.params.dataSeries || parentIndicator.params.id;
		}

		for (let index = 0; count <= period; index++, count++) {
			sumX += count;
			sumX2 += count * count;
			temp = data[index][seriesId];
			sumY += temp;
			sumXY += count * temp;
		}

		const d = period * sumX2 - sumX * sumX;
		const m = (period * sumXY - sumX * sumY) / d; // Slope
		const b = (sumX2 * sumY - sumX * sumXY) / d; // Intercept

		regressionData['Beginning'] = b + m * 1;
		regressionData['End'] = b + m * period;
		regressionData['Slope'] = m;

		return regressionData;
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const lrData = this.getLinearRegression(data.linearregression);
		const ctx = this.panel.rootContext;
		const size = this.panel.size;
		const style = this.params.style;

		// Calculate coordinates for line
		const x0 = size.padding.left;
		const y0 = this.yScale(lrData.Beginning);
		const x1 = size.padding.left + size.width;
		const y1 = this.yScale(lrData.End);

		if (!isNaN(y0) && !isNaN(y1)) {
			// Only clip the line if it would go past the panel height
			if (y0 > size.height || y1 > size.height) {
				this.panel.clipPanel();
			}
			ctx.beginPath();
			ctx.strokeStyle = style.color;
			ctx.lineWidth = style.width;
			if (style.lineDash) {
				ctx.setLineDash(style.lineDash.split(/\s*,\s*/));
			}
			subpixel.moveTo(ctx, x0, y0);
			subpixel.lineTo(ctx, x1, y1);
			ctx.stroke();
			ctx.setLineDash([]);
		}
	}
}
